import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiCreditCard, FiHistory, FiLifeBuoy, FiFileText, FiZap } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import Header from '../components/Header';
import StatsCard from '../components/StatsCard';
import TestHistory from '../components/TestHistory';
import CreditHistory from '../components/CreditHistory';
import UserPaymentHistory from '../components/UserPaymentHistory';
import BuyCreditsModal from '../components/BuyCreditsModal';
import RequestTestModal from '../components/RequestTestModal';
import SettingsModal from '../components/SettingsModal';
import SupportSystem from '../components/SupportSystem';

const UserDashboard = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('tests');
  const [refreshKey, setRefreshKey] = useState(0);
  const [testsCount, setTestsCount] = useState(0);
  
  // Modals
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('app_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (err) {
      console.error('Erro ao buscar perfil:', err);
    }
  }, []);

  const fetchTestsCount = useCallback(async (userId) => {
    if (!userId) return;
    const { count, error } = await supabase
      .from('app_tests')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (!error) setTestsCount(count || 0);
  }, []);

  useEffect(() => {
    const getInitialData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
        fetchTestsCount(session.user.id);
      }
    };

    getInitialData();

    const handleFocus = () => {
      if (user?.id) {
        fetchProfile(user.id);
        fetchTestsCount(user.id);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchProfile, fetchTestsCount, user?.id]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    if (user?.id) {
      fetchProfile(user.id);
      fetchTestsCount(user.id);
    }
  };

  const navItems = [
    { id: 'tests', label: 'Meus Testes', icon: FiHistory },
    { id: 'credits', label: 'Extrato', icon: FiCreditCard },
    { id: 'payments', label: 'Faturas', icon: FiFileText },
    { id: 'support', label: 'Suporte', icon: FiLifeBuoy },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-32 lg:pb-10">
      <Header 
        user={user} 
        profile={profile} 
        onLogout={onLogout}
        onOpenSettings={() => setIsSettingsOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 pt-24 md:pt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT COLUMN: Actions and Stats (Top on Mobile) */}
          <aside className="lg:col-span-4 space-y-6">
            <StatsCard 
              title="Saldo Disponível" 
              value={profile?.credits || 0} 
              unit="Créditos"
              icon={FiPlus}
              color="indigo"
            />
            
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
              <button 
                onClick={() => setIsRequestModalOpen(true)}
                className="group relative h-24 lg:h-32 bg-slate-900 rounded-[32px] overflow-hidden flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 shadow-2xl shadow-slate-200"
              >
                <div className="absolute inset-0 bg-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="bg-white/10 p-3 rounded-2xl">
                  <SafeIcon icon={FiZap} className="text-white text-xl" />
                </div>
                <span className="text-white font-black uppercase italic text-xs tracking-tight">Novo Teste</span>
              </button>

              <button 
                onClick={() => setIsBuyModalOpen(true)}
                className="group relative h-24 lg:h-32 bg-white rounded-[32px] border-2 border-slate-100 overflow-hidden flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 shadow-sm"
              >
                <div className="bg-indigo-50 p-3 rounded-2xl group-hover:bg-indigo-100 transition-colors">
                  <SafeIcon icon={FiPlus} className="text-indigo-600 text-xl" />
                </div>
                <span className="text-slate-900 font-black uppercase italic text-xs tracking-tight">Comprar Créditos</span>
              </button>
            </div>

            <div className="hidden lg:grid grid-cols-2 gap-4">
              <StatsCard 
                title="Total Gerado" 
                value={testsCount} 
                icon={FiHistory}
                color="white"
              />
              <StatsCard 
                title="Status" 
                value={profile?.status === 'active' ? 'Ativo' : 'Inativo'} 
                icon={FiLifeBuoy}
                color="white"
              />
            </div>
          </aside>

          {/* RIGHT COLUMN: Content and Tabs */}
          <section className="lg:col-span-8 space-y-6">
            {/* Desktop Tabs */}
            <div className="hidden lg:flex items-center gap-2 bg-white p-2 rounded-[28px] w-fit shadow-sm border border-slate-100">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center gap-2 px-6 py-3.5 rounded-2xl font-black uppercase italic text-[11px] tracking-wider transition-all ${
                    activeTab === item.id 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {activeTab === 'tests' && <TestHistory userId={user?.id} refreshKey={refreshKey} profile={profile} />}
              {activeTab === 'credits' && <CreditHistory userId={user?.id} refreshKey={refreshKey} />}
              {activeTab === 'payments' && <UserPaymentHistory userId={user?.id} />}
              {activeTab === 'support' && <SupportSystem userId={user?.id} />}
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE BOTTOM NAVIGATION: Fixed at bottom */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-100 px-6 py-4 z-50 rounded-t-[40px] shadow-2xl">
        <div className="flex justify-between items-center max-w-md mx-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center gap-1.5 transition-all ${
                activeTab === item.id ? 'text-indigo-600 scale-110' : 'text-slate-300'
              }`}
            >
              <div className={`p-2.5 rounded-2xl transition-all ${
                activeTab === item.id ? 'bg-indigo-50 shadow-inner' : ''
              }`}>
                <SafeIcon icon={item.icon} className="text-xl" />
              </div>
              <span className="text-[9px] font-black uppercase tracking-tighter">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <BuyCreditsModal 
        isOpen={isBuyModalOpen} 
        onClose={() => setIsBuyModalOpen(false)} 
        profile={profile}
        onOpenSettings={() => {
          setIsBuyModalOpen(false);
          setIsSettingsOpen(true);
        }}
      />
      <RequestTestModal 
        isOpen={isRequestModalOpen} 
        onClose={() => setIsRequestModalOpen(false)} 
        profile={profile}
        onSuccess={handleRefresh}
      />
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        profile={profile}
        onUpdate={() => user?.id && fetchProfile(user.id)}
      />
    </div>
  );
};

export default UserDashboard;